import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
} from '@heroui/react';
import { Folder as FolderIcon, FileQuestion } from 'lucide-react';
import { toast } from 'sonner';
import { useFolders } from '@/api/folders';
import { useMoveKnowledge } from '@/api/knowledge';
import type { KnowledgeItem } from '@estimator/shared';

interface MoveToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  item: KnowledgeItem;
}

export default function MoveToFolderModal({
  isOpen,
  onClose,
  projectId,
  item,
}: MoveToFolderModalProps) {
  const { data: foldersResponse } = useFolders(projectId, 'root');
  const folders = foldersResponse?.data ?? [];
  const moveMutation = useMoveKnowledge(projectId);

  const handleMove = (folderId: string | null, folderName: string) => {
    // No-op if already in that folder
    if (folderId === (item.folder_id ?? null)) {
      onClose();
      return;
    }

    moveMutation.mutate(
      { id: item.id, folderId },
      {
        onSuccess: () => {
          toast.success(`"${item.title}" moved to ${folderName}`);
          onClose();
        },
        onError: (err) => {
          toast.error(`Failed to move: ${err.message}`);
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
        <ModalHeader
          className="text-sm font-semibold"
          style={{ color: 'var(--text-primary)' }}
        >
          Move &ldquo;{item.title}&rdquo;
        </ModalHeader>
        <ModalBody className="pb-4">
          <div className="flex flex-col gap-0.5">
            {folders.map((folder) => {
              const isCurrent = folder.id === item.folder_id;
              return (
                <button
                  key={folder.id}
                  onClick={() => handleMove(folder.id, folder.name)}
                  disabled={moveMutation.isPending}
                  className="flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors hover:bg-[var(--bg-hover)]"
                  style={{
                    color: isCurrent ? 'var(--accent)' : 'var(--text-primary)',
                    fontWeight: isCurrent ? 600 : 400,
                  }}
                >
                  <FolderIcon
                    size={16}
                    style={{ color: isCurrent ? 'var(--accent)' : 'var(--blue)', flexShrink: 0 }}
                  />
                  <span className="flex-1 truncate">{folder.name}</span>
                  {isCurrent && (
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      current
                    </span>
                  )}
                </button>
              );
            })}

            {/* Unfiled option */}
            <button
              onClick={() => handleMove(null, 'Unfiled')}
              disabled={moveMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 rounded text-sm text-left transition-colors hover:bg-[var(--bg-hover)]"
              style={{
                color: item.folder_id == null ? 'var(--accent)' : 'var(--text-muted)',
                fontWeight: item.folder_id == null ? 600 : 400,
              }}
            >
              <FileQuestion
                size={16}
                style={{
                  color: item.folder_id == null ? 'var(--accent)' : 'var(--text-muted)',
                  flexShrink: 0,
                }}
              />
              <span className="flex-1 truncate">Unfiled</span>
              {item.folder_id == null && (
                <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                  current
                </span>
              )}
            </button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
